import { ANNOTATION_VALUE } from "@/constants/annotation-values"

import type { ChartWidgetConfig } from "../../types/chart"
import { COLOR_SCHEME_VARIANTS } from "./colorSchemes"

/**
 * Flattened lookup map for auto-detection of string values across all schemes.
 * Built once and cached for O(1) lookups. Later schemes override earlier ones
 * in case of conflicts (e.g., TRUE appears in multiple schemes).
 *
 * Uses variant 0 (default) of each scheme for auto-detection.
 *
 * Priority order for scheme conflicts:
 * 1. yes-no-unknown (most common)
 * 2. pass-fail-skip
 * 3. pass-fail
 * 4. true-false
 * 5. good-bad-neutral
 * 6. high-medium-low
 */
const AUTO_DETECT_COLOR_MAP = (() => {
  const map: Record<string, string> = {}
  const schemeOrder: Array<keyof typeof COLOR_SCHEME_VARIANTS> = [
    "high-medium-low",
    "good-bad-neutral",
    "true-false",
    "pass-fail",
    "pass-fail-skip",
    "yes-no-unknown", // Highest priority - applied last
  ]

  schemeOrder.forEach((schemeName) => {
    const variants = COLOR_SCHEME_VARIANTS[schemeName]
    // Use variant 0 (default) for auto-detection
    const defaultScheme = variants[0]
    Object.entries(defaultScheme).forEach(([key, color]) => {
      map[key] = color
    })
  })

  return map
})()

/**
 * Resolves the color for a string value based on priority:
 * 1. Custom color map (explicit override - highest priority)
 * 2. Configured color scheme (with variant selection if groupId provided)
 * 3. target_values-based scheme (includes "NO" → no-yes-unknown, else → yes-no-unknown)
 * 4. Auto-detected scheme (with variant selection if groupId provided)
 * 5. Default index-based color assignment (fallback)
 *
 * @param stringValue - The string value to get a color for
 * @param index - The index position (fallback when no scheme is defined)
 * @param config - Widget configuration containing color scheme settings
 * @param defaultColors - Default color palette to use for fallback
 * @param groupId - Optional group identifier (agent/persona ID) for variant selection
 * @param allGroupIds - Optional array of all group identifiers in order
 * @param targetValues - When provided, overrides scheme: includes "NO" → no-yes-unknown, else → yes-no-unknown
 * @returns Hex color string
 */
export function getColorForStringValue(
  stringValue: string,
  index: number,
  config: ChartWidgetConfig,
  defaultColors: readonly string[],
  groupId?: string,
  allGroupIds?: readonly string[],
  targetValues?: string[],
): string {
  // Calculate group index for variant selection (0-3, cycling)
  const groupIndex = groupId && allGroupIds ? allGroupIds.indexOf(groupId) : -1
  const variantIndex = groupIndex >= 0 ? groupIndex % 4 : 0

  // Priority 1: Custom color map (highest priority)
  if (config.customColorMap?.[stringValue]) {
    return config.customColorMap[stringValue]
  }

  // Priority 2: Predefined color scheme (explicit config)
  if (config.colorScheme) {
    const variants = COLOR_SCHEME_VARIANTS[config.colorScheme]
    if (variants) {
      const scheme = variants[variantIndex]
      if (scheme && stringValue in scheme) {
        return scheme[stringValue as keyof typeof scheme]
      }
    }
  }

  // Priority 3: target_values-based scheme
  if (
    targetValues !== undefined &&
    (
      [
        ANNOTATION_VALUE.YES,
        ANNOTATION_VALUE.NO,
        ANNOTATION_VALUE.UNKNOWN,
      ] as string[]
    ).includes(stringValue)
  ) {
    const schemeName = targetValues.includes(ANNOTATION_VALUE.NO)
      ? "no-yes-unknown"
      : "yes-no-unknown"
    const variants = COLOR_SCHEME_VARIANTS[schemeName]
    const scheme = variants[variantIndex]
    if (scheme && stringValue in scheme) {
      return scheme[stringValue as keyof typeof scheme]
    }
  }

  // Priority 4: Auto-detect from all schemes
  if (groupIndex >= 0) {
    // Multi-group mode: try to find in variants
    for (const schemeName of Object.keys(COLOR_SCHEME_VARIANTS) as Array<
      keyof typeof COLOR_SCHEME_VARIANTS
    >) {
      // Skip inverted scheme in auto-detect (only used explicitly)
      if (schemeName === "no-yes-unknown") {
        continue
      }
      const variants = COLOR_SCHEME_VARIANTS[schemeName]
      const scheme = variants[variantIndex]
      if (scheme && stringValue in scheme) {
        return scheme[stringValue as keyof typeof scheme]
      }
    }
  } else if (stringValue in AUTO_DETECT_COLOR_MAP) {
    // Single-group mode: use pre-built auto-detect map
    return AUTO_DETECT_COLOR_MAP[stringValue]
  }

  // Priority 5: Fall back to index-based color assignment
  return defaultColors[index % defaultColors.length]
}
